/**
 * DetalleMesDialog (rc99) — drill-down de un mes del módulo Análisis:
 * QUIÉNES fueron las altas y las bajas, con cliente, plan, fecha y
 * (en bajas) el motivo de cancelación. Cada fila linkea a la ficha.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { useDetalleMes, type DetalleMesItem } from "./api";

type Props = {
  mes: string | null;          // "YYYY-MM" — null = cerrado
  mesLabel: string;
  empresaId: number | null;
  onClose: () => void;
};

export function DetalleMesDialog({ mes, mesLabel, empresaId, onClose }: Props) {
  const [tipo, setTipo] = useState<"altas" | "bajas">("altas");
  const { data, isLoading } = useDetalleMes(mes, tipo, empresaId);

  return (
    <Dialog open={mes !== null} onOpenChange={(v) => { if (!v) onClose(); }} size="2xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Detalle de {mesLabel}
          </DialogTitle>
          <DialogDescription>
            Contratos (no clientes) que fueron alta o baja en el mes — un cliente viejo con contrato nuevo también aparece. Click en una fila
            abre el contrato.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs altas/bajas */}
        <div className="flex items-center gap-1 border-b">
          {(["altas", "bajas"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              className={cn(
                "flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors capitalize",
                t === tipo
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "altas"
                ? <TrendingUp size={12} className="text-electric" />
                : <TrendingDown size={12} className="text-danger" />}
              {t}
              {data && t === tipo && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  ({data.total})
                </span>
              )}
            </button>
          ))}
        </div>

        {isLoading || !data ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin" />
            cargando…
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-md border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            Sin {tipo} en {mesLabel}.
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto rounded-md border divide-y">
            {data.items.map((it) => (
              <FilaDetalle key={it.contrato_id} item={it} tipo={tipo} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FilaDetalle({ item, tipo }: { item: DetalleMesItem; tipo: "altas" | "bajas" }) {
  return (
    <Link
      to={`/contratos/${item.contrato_id}`}
      className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-muted/30"
    >
      <span className="font-mono text-muted-foreground shrink-0">{item.fecha}</span>
      <span className="font-mono shrink-0">{item.codigo}</span>
      <span className="flex-1 min-w-0 truncate">
        {item.cliente_nombre}
        {/* rc141 — alta de contrato ≠ cliente nuevo */}
        {tipo === "altas" && item.cliente_nuevo === false && (
          <span
            className="ml-1.5 rounded bg-muted px-1 py-px font-mono text-[9px] uppercase tracking-wider text-muted-foreground"
            title={item.cliente_desde ? `Ya era cliente desde ${item.cliente_desde}` : "Ya era cliente"}
          >
            ya cliente{item.cliente_desde ? ` · ${item.cliente_desde.slice(0, 7)}` : ""}
          </span>
        )}
        {tipo === "altas" && item.cliente_nuevo === true && (
          <span className="ml-1.5 rounded bg-electric-soft px-1 py-px font-mono text-[9px] uppercase tracking-wider text-electric">
            nuevo
          </span>
        )}
      </span>
      <span className="font-mono text-[10px] text-muted-foreground shrink-0 hidden sm:block">
        {item.plan_nombre}
      </span>
      {tipo === "bajas" ? (
        <span
          className="max-w-[180px] truncate text-[10px] text-muted-foreground italic"
          title={item.motivo_cancelacion ?? undefined}
        >
          {item.motivo_cancelacion || "sin motivo"}
        </span>
      ) : (
        <Badge
          variant={item.estatus === "activo" ? "success"
            : item.estatus === "cancelado" ? "destructive" : "warning"}
          className="text-[9px]"
        >
          {item.estatus}
        </Badge>
      )}
    </Link>
  );
}
